import React from 'react';
import {Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {useDispatch} from 'react-redux';
import {Trans} from '@lingui/react/macro';
import {cordwoodApi} from '../../../apis/Cordwood.api.ts';
import {logApi} from '../../../apis/Log.api.ts';
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import {PieChart} from '@mui/x-charts/PieChart';
import {t} from "@lingui/core/macro";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {timberTypeApi} from "../../../apis/TimberType.api.ts";
import {stringToColor} from "../../../../utils/color.ts";

const StorageShipmentPies = ({ filter, showOnlyShipped = false }:{
    filter: any, showOnlyShipped: boolean,
}) => {

    const dispatch = useDispatch();
    const [logAgg, setLogAgg] = React.useState<Record<string, { shipped: number; notShipped: number }>>({});
    const [cordAgg, setCordAgg] = React.useState<Record<string, { shipped: number; notShipped: number }>>({});
    const [typeLabels, setTypeLabels] = React.useState<Record<string, string>>({});
    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        let mounted = true;
        const run = async () => {
            setLoading(true);
            try {
                const [logsSh, logsNs, cordsSh, cordsNs, timberTypes] = await Promise.all([
                    (!filter.shipmentType || filter.shipmentType == 'Logs') ? dispatch(logApi.endpoints.getGroup.initiate({ ...filter, shipmentDefined: true } as any)).unwrap().catch(() => []) : [],
                    (!filter.shipmentType || filter.shipmentType == 'Logs') && !showOnlyShipped ? dispatch(logApi.endpoints.getGroup.initiate({ ...filter, shipmentDefined: false } as any)).unwrap().catch(() => []) : [],
                    (!filter.shipmentType || filter.shipmentType == 'Cordwood') ? dispatch(cordwoodApi.endpoints.getGroup.initiate({ ...filter, shipmentDefined: true } as any)).unwrap().catch(() => []) : [],
                    (!filter.shipmentType || filter.shipmentType == 'Cordwood') && !showOnlyShipped ? dispatch(cordwoodApi.endpoints.getGroup.initiate({ ...filter, shipmentDefined: false } as any)).unwrap().catch(() => []) : [],
                    dispatch(timberTypeApi.endpoints.getTimberTypeList.initiate({pageSize: 1000} as any)).unwrap().catch(() => []),
                ]);
                const types: Record<string, string> = {};
                const logMap: Record<string, { shipped: number; notShipped: number }> = {};
                const cordMap: Record<string, { shipped: number; notShipped: number }> = {};

                for (const r of (Array.isArray(timberTypes?.items) ? timberTypes.items : [])) {
                    types[r.id] = r.reportNameShort || r.name || r.id;
                }
                for (const r of (Array.isArray(logsSh) ? logsSh : [])) {
                    const t = r.timberTypeId?.toString?.() || String(r.timberTypeId);
                    if (!types[t]) types[t] = r.timberType?.name || t;
                    if (!logMap[t]) logMap[t] = { shipped: 0, notShipped: 0 };
                    logMap[t].shipped += r.totalVolume || 0;
                }
                for (const r of (Array.isArray(logsNs) ? logsNs : [])) {
                    const t = r.timberTypeId?.toString?.() || String(r.timberTypeId);
                    if (!types[t]) types[t] = r.timberType?.name || t;
                    if (!logMap[t]) logMap[t] = { shipped: 0, notShipped: 0 };
                    logMap[t].notShipped += r.totalVolume || 0;
                }
                for (const r of (Array.isArray(cordsSh) ? cordsSh : [])) {
                    const t = r.timberTypeId?.toString?.() || String(r.timberTypeId);
                    if (!types[t]) types[t] = r.timberType?.name || t;
                    if (!cordMap[t]) cordMap[t] = { shipped: 0, notShipped: 0 };
                    cordMap[t].shipped += r.totalVolume || 0;
                }
                for (const r of (Array.isArray(cordsNs) ? cordsNs : [])) {
                    const t = r.timberTypeId?.toString?.() || String(r.timberTypeId);
                    if (!types[t]) types[t] = r.timberType?.name || t;
                    if (!cordMap[t]) cordMap[t] = { shipped: 0, notShipped: 0 };
                    cordMap[t].notShipped += r.totalVolume || 0;
                }
                if (mounted) {
                    setLogAgg(logMap)
                    setCordAgg(cordMap)
                    setTypeLabels(types)
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        run();

        return () => { mounted = false; };
    }, [dispatch, filter]);

    const makeSeries = (agg: Record<string,{ shipped:number; notShipped:number }>) => {
        const inner = Object.keys(agg).map((tId) => {
            const label = typeLabels[tId] || tId;
            return {
                id: `t-${tId}`,
                label,
                value: (agg[tId].shipped + agg[tId].notShipped),
                color: stringToColor(label),
            };
        }).filter(p => Number.isFinite(p.value) && p.value > 0);

        const outer = Object.keys(agg).flatMap((tId) => {
            const label = typeLabels[tId] || tId;
            const baseColor = stringToColor(label);
            return [
                {
                    id: `t-${tId}-sh`,
                    label: `${label} • ${t('Shipped')}`,
                    value: agg[tId].shipped,
                    color: baseColor,
                },
                {
                    id: `t-${tId}-ns`,
                    label: `${label} • ${t('Not shipped')}`,
                    value: agg[tId].notShipped,
                    color: alpha(baseColor, 0.5),
                },
            ];
        }).filter(p => Number.isFinite(p.value) && p.value > 0);
        return { inner, outer };
    };

    const logsSeries = makeSeries(logAgg);
    const cordsSeries = makeSeries(cordAgg);

    if (loading)
        return (
            <GradientCircularProgress />
        )

    const logs = showOnlyShipped ? [
        { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: logsSeries.inner, arcLabel: (item) => fixedFormat(item.value) },
    ] : [
        { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: logsSeries.inner, arcLabel: (item) => fixedFormat(item.value) },
        { innerRadius: 150, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: logsSeries.outer, arcLabel: (item) => fixedFormat(item.value) },
    ]

    const cordwood = showOnlyShipped ? [
        { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: cordsSeries.inner, arcLabel: (item) => fixedFormat(item.value) },
    ] : [
        { innerRadius: 30, outerRadius: 147, paddingAngle: 0, cornerRadius: 6, data: cordsSeries.inner, arcLabel: (item) => fixedFormat(item.value) },
        { innerRadius: 150, outerRadius: 200, paddingAngle: 0, cornerRadius: 6, data: cordsSeries.outer, arcLabel: (item) => fixedFormat(item.value) },
    ]

    return (
        <Stack spacing={2} sx={{ mt: 2 }}>
            { (!filter.shipmentType || filter.shipmentType == 'Logs') &&
                <Stack>
                    <Typography variant="subtitle2"><Trans>Logs</Trans></Typography>
                    <PieChart
                        height={420}
                        series={logs}
                        slotProps={{ legend: { hidden: false } }}
                    />
                </Stack>
            }
            { (!filter.shipmentType || filter.shipmentType == 'Cordwood') &&
                <Stack>
                    <Typography variant="subtitle2"><Trans>Cordwood</Trans></Typography>
                    <PieChart
                        height={420}
                        series={cordwood}
                        slotProps={{ legend: { hidden: false } }}
                    />
                </Stack>
            }
        </Stack>
    );
};

export default StorageShipmentPies;